import { sortOrganisations } from "@/utils/organisationSort";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CountrySelect } from "@/components/ui/country-select";
import { useUpdateContact, useDeleteContact } from "@/hooks/use-contacts";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Contact } from "@/hooks/use-contacts";
import { useRoleBasedAccess, usePartnerOrganizations } from "@/hooks/use-role-based-access";
import { Trash2 } from "lucide-react";

interface ContactEditModalProps {
  contact: Contact;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ContactEditModal = ({ contact, isOpen, onClose, onSuccess }: ContactEditModalProps) => {
  const [formData, setFormData] = useState({
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    email: contact.email || "",
    phone: contact.phone || "",
    job_title: contact.job_title || "",
    country: contact.country || "",
    notes: contact.notes || "",
    organization_id: contact.organization_id || "",
    contact_type: contact.contact_type || "Buyer" as const,
    relationship_status: contact.relationship_status || "Prospect" as const,
    user_status: contact.user_status || "inactive" as const,
    internal_owner_id: contact.internal_owner_id || null,
    external_owner_id: contact.external_owner_id || null,
    intended_system_role: contact.intended_system_role || "standard" as const
  });

  const [organizations, setOrganizations] = useState<Array<{id: string, name: string}>>([]);
  const [staff, setStaff] = useState<Array<{id: string, first_name: string, last_name: string}>>([]);
  const [partners, setPartners] = useState<Array<{id: string, first_name: string, last_name: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const { userOrganizationId, isSystemAdmin, isPartner } = useRoleBasedAccess();
  const { data: partnerOrganizations = [] } = usePartnerOrganizations();

  useEffect(() => {
    const fetchOrganizations = async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      
      if (error) {
        console.error('Error fetching organizations:', error);
        return;
      }
      
      setOrganizations(data || []);
    };

    const fetchStaff = async () => {
      const { data, error } = await supabase
        .from('crm_staff')
        .select('id, first_name, last_name')
        .order('first_name');
      
      if (error) {
        console.error('Error fetching staff:', error);
        return;
      }
      
      setStaff(data || []);
    };

    const fetchPartners = async () => {
      const { data, error } = await supabase
        .from('crm_partners')
        .select('id, first_name, last_name')
        .eq('status', 'Active')
        .order('first_name');
      
      if (error) {
        console.error('Error fetching partners:', error);
        return;
      }
      
      setPartners(data || []);
    };

    if (isOpen) {
      fetchOrganizations();
      fetchStaff();
      fetchPartners();
    }
  }, [isOpen]);

  // Filter organizations based on user role - matches ContactModal pattern
  const filteredOrganizations = useMemo(() => {
    // Superusers and internal staff see all organizations
    if (isSystemAdmin) {
      return sortOrganisations(organizations, org => org.name);
    }

    // Partners only see organizations reached through their own contacts/deals
    if (isPartner) {
      const partnerOrgIds = new Set((partnerOrganizations || []).map((org: any) => org.id));
      const scoped = organizations.filter(org => partnerOrgIds.has(org.id));
      // Keep the contact's current organization selectable so editing never loses it
      if (contact?.organization_id && !partnerOrgIds.has(contact.organization_id)) {
        const current = organizations.find(org => org.id === contact.organization_id);
        if (current) scoped.push(current);
      }
      return sortOrganisations(scoped, org => org.name);
    }

    // Admins and standard users only see their own organization
    if (userOrganizationId) {
      return organizations.filter(org => org.id === userOrganizationId);
    }
    
    // Fallback: return empty array if no organization ID
    return [];
  }, [organizations, isSystemAdmin, isPartner, partnerOrganizations, userOrganizationId, contact?.organization_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
          await updateContact.mutateAsync({
        id: contact.id,
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          job_title: formData.job_title,
          country: formData.country,
          notes: formData.notes,
          organization_id: formData.organization_id,
          contact_type: formData.contact_type,
          relationship_status: formData.relationship_status,
          user_status: formData.user_status,
          internal_owner_id: formData.internal_owner_id || null,
          external_owner_id: formData.external_owner_id || null,
          intended_system_role: formData.intended_system_role
        }
      });

      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value === "none" ? null : value };
      
      // Mutual exclusion: clear other owner when one is selected
      if (field === 'internal_owner_id' && value && value !== "none") {
        newData.external_owner_id = null;
      } else if (field === 'external_owner_id' && value && value !== "none") {
        newData.internal_owner_id = null;
      }
      
      return newData;
    });
  };

  const handleDelete = async () => {
    try {
      await deleteContact.mutateAsync(contact.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">Edit Contact</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <CountrySelect
              value={formData.country}
              onValueChange={(value) => handleInputChange('country', value)}
            />
          </div>

          {isSystemAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_type">Contact Type</Label>
                <Select 
                  value={formData.contact_type} 
                  onValueChange={(value: "Buyer" | "Influencer" | "Industry Expert") => handleInputChange('contact_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Buyer">Buyer</SelectItem>
                    <SelectItem value="Influencer">Influencer</SelectItem>
                    <SelectItem value="Industry Expert">Industry Expert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="relationship_status">Relationship Status</Label>
                <Select 
                  value={formData.relationship_status} 
                  onValueChange={(value: "Prospect" | "Customer") => handleInputChange('relationship_status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Prospect">Prospect</SelectItem>
                    <SelectItem value="Customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="user_status">User Status</Label>
              <Select 
                value={formData.user_status} 
                onValueChange={(value: "no_account" | "invitation_sent" | "active" | "inactive") => handleInputChange('user_status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_account">No Account</SelectItem>
                  <SelectItem value="invitation_sent">Invitation Sent</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="intended_system_role">System Access Level</Label>
              <Select 
                value={formData.intended_system_role || "standard"} 
                onValueChange={(value: "standard" | "editor" | "admin") => handleInputChange('intended_system_role', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard User</SelectItem>
                  <SelectItem value="editor">Standard User with Edit Rights</SelectItem>
                  <SelectItem value="admin">Admin User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization_id">Organization</Label>
            <Select 
              value={formData.organization_id} 
              onValueChange={(value) => handleInputChange('organization_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {filteredOrganizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSystemAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="internal_owner_id">Internal Owner (Staff)</Label>
                <Select 
                  value={formData.internal_owner_id || "none"} 
                  onValueChange={(value) => handleInputChange('internal_owner_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select internal owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Clear Selection</SelectItem>
                    {staff.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="external_owner_id">External Owner (Partner)</Label>
                <Select 
                  value={formData.external_owner_id || "none"} 
                  onValueChange={(value) => handleInputChange('external_owner_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select external owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Clear Selection</SelectItem>
                    {partners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.first_name} {partner.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-between">
            {(contact.user_status === 'no_account' || !contact.user_status) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete {contact.first_name} {contact.last_name}? 
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Contact
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            
            <div className="flex-1" />
            
            <div className="space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
              >
                {isLoading ? "Updating..." : "Update Contact"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
